import { useEffect, useState } from 'react';

/**
 * Subscribe to a CSS media query and re-render when it flips.
 *
 * SSR-safe: defaults to `false` when `window` is undefined (matches the
 * "desktop" assumption — mobile gate won't flash on initial paint).
 *
 *   const isMobile = useMediaQuery('(max-width: 899px)');
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);

    // Keep state in sync with the current query (important if `query` changes).
    setMatches(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

/**
 * Convenience wrapper for the 900px gate defined in DESIGN.md §8.
 * Anything below that shows the desktop-required screen.
 */
export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 899px)');
}
